import { Modal } from "./Modal";
import { Logo } from "./Logo";
import { EXPLORADOR } from "../lib/enlaces";
import { useT } from "../lib/i18n";
import type { RedKeel } from "../config/networks";
import type { Progreso } from "../hooks/useAcciones";

/**
 * El modal que acompaña a cada operación: mientras se firma y se confirma en la cadena enseña el
 * logo de Keel «cargando» con el paso en el que va, y al terminar se queda en una confirmación
 * clara (o en el error, si lo hubo). Antes solo giraba un spinner en el botón y el usuario no sabía
 * si su wallet le estaba pidiendo algo o si ya estaba hecho.
 *
 * MEJORA 2026-08-30: mejor tipografía, descripciones contextuales por fase, transiciones suaves.
 */

// Texto por acción: el gerundio para la carga y el título/texto de la confirmación al terminar.
const TX: Record<string, { verbo: string; ok: string; okSub: string }> = {
  anadirFondos: { verbo: "Añadiendo fondos", ok: "Fondos añadidos", okSub: "Tu posición creció; el objetivo a recuperar subió con ella." },
  cargarGas: { verbo: "Cargando la bolsa de gas", ok: "Gas cargado", okSub: "El robot ya puede reajustar." },
  devolverGas: { verbo: "Devolviendo el gas", ok: "Gas devuelto", okSub: "Lo que no se gastó ya está en tu wallet." },
  anadirReserva: { verbo: "Apartando a la reserva", ok: "Reserva apartada", okSub: "El robot la irá soltando según caiga el precio." },
  retirarGanancias: { verbo: "Cobrando tus ganancias", ok: "Ganancias cobradas", okSub: "Ya están en tu wallet. Tu capital y tu posición no se tocaron." },
  reinvertirGanancias: { verbo: "Reinvirtiendo tus ganancias", ok: "Ganancias reinvertidas", okSub: "Volvieron a la posición a trabajar." },
  quitarLiquidez: { verbo: "Sacando esa parte", ok: "Retirado a tu wallet", okSub: "El resto sigue trabajando." },
  recenterTight: { verbo: "Volviendo al rango estrecho", ok: "De vuelta al rango estrecho", okSub: "Se re-centró tu posición al ancho base y tu activo no se vendió." },
  sacarTodo: { verbo: "Cerrando el vault", ok: "Vault cerrado", okSub: "Tienes todo en tu wallet. Puedes volver a abrirlo cuando quieras." },
  recargarCuota: { verbo: "Recargando la cuota", ok: "Cuota recargada", okSub: "El robot vuelve a tener reajustes disponibles." },
  // ajustes
  guardarRango: { verbo: "Guardando el rango", ok: "Rango guardado", okSub: "El robot lo usará en el próximo reajuste." },
  guardarGuardias: { verbo: "Guardando los guardias", ok: "Guardias guardados", okSub: "Tu vault se protege mejor contra manipulación de precios." },
  guardarRecuperacion: { verbo: "Guardando la recuperación", ok: "Recuperación guardada", okSub: "El robot manejará las caídas como configuraste." },
  guardarColchon: { verbo: "Guardando el colchón", ok: "Colchón guardado", okSub: "El rango de recuperación apuntará más alto." },
  guardarReinyeccion: { verbo: "Guardando la reinyección", ok: "Reinyección guardada", okSub: "El robot soltará la reserva según tus reglas." },
  cambiarReinversion: { verbo: "Cambiando la reinversión", ok: "Reinversión actualizada", okSub: "Las ganancias futuras seguirán tu nueva regla." },
  pausar: { verbo: "Actualizando el robot", ok: "Hecho", okSub: "El estado del robot ha cambiado." },
  cambiarOperador: { verbo: "Cambiando el operador", ok: "Operador cambiado", okSub: "La nueva dirección puede reajustar tu posición." },
  detenerRobot: { verbo: "Deteniendo el robot", ok: "Robot detenido", okSub: "El vault sigue siendo tuyo; nadie lo reajusta hasta que pongas operador." },
  actualizarLogica: { verbo: "Actualizando la lógica", ok: "Lógica actualizada", okSub: "El vault ejecuta la versión nueva del código." },
};

export function ModalProceso({
  progreso, cerrar, red,
}: {
  progreso: Progreso | null;
  cerrar: () => void;
  red: RedKeel;
}) {
  const t = useT();
  if (!progreso) return null;
  const info = TX[progreso.nombre] ?? { verbo: "Procesando", ok: "Listo", okSub: "La operación se completó." };
  const cargando = progreso.fase === "aprobando" || progreso.fase === "firmando" || progreso.fase === "enviada";
  const tx = progreso.hash ? `${EXPLORADOR[red.clave]}/tx/${progreso.hash}` : null;

  // Titular y subtítulo según el paso. «Aprobando» es la 1ª de dos firmas; «firmando» es la operación.
  const enc = progreso.fase === "aprobando" ? t("Dando permiso al token") : `${t(info.verbo)}…`;
  const sub =
    progreso.fase === "aprobando"
      ? t("Primero autorizas al vault a mover tu token. Es la primera de dos firmas — confírmala en tu wallet.")
      : progreso.fase === "firmando"
        ? t("Ahora firma la operación en tu wallet. Nada se mueve hasta que la des.")
        : t("Firmado y enviado. Esperando a que la cadena lo confirme…");

  const titulo = cargando ? t("En marcha") : progreso.fase === "ok" ? t(info.ok) : t("No se pudo completar");

  return (
    <Modal
      abierto
      cerrar={cerrar}
      titulo={titulo}
      ancho={440}
      pie={
        cargando ? undefined : (
          <button className="btn principal" style={{ marginLeft: "auto" }} onClick={cerrar} data-foco>
            {t("Listo")}
          </button>
        )
      }
    >
      {cargando ? (
        <div className="proceso proceso-cargando">
          <div className="proceso-logo" aria-hidden><Logo size={52} /></div>
          {progreso.fase !== "enviada" && (
            <span className="proceso-firma">
              {progreso.dos ? (progreso.fase === "aprobando" ? t("Firma 1 de 2") : t("Firma 2 de 2")) : t("Firma en tu wallet")}
            </span>
          )}
          {progreso.fase === "enviada" && (
            <span className="proceso-firma confirmando">{t("Confirmando en la cadena…")}</span>
          )}
          <h3>{enc}</h3>
          <p className="detalle">{sub}</p>
          {tx && <a className="proceso-tx" href={tx} target="_blank" rel="noreferrer">{t("Ver transacción en el explorador ↗")}</a>}
        </div>
      ) : progreso.fase === "ok" ? (
        <div className="proceso proceso-ok">
          <div className="proceso-check" aria-hidden>✓</div>
          <h3>{t(info.ok)}</h3>
          {info.okSub && <p className="detalle">{t(info.okSub)}</p>}
          {/* Aviso dinámico de la operación (p. ej. "no convertí a estable por slippage alto"): la
              operación salió bien, pero hay algo que el usuario debe saber. */}
          {progreso.nota && <p className="detalle proceso-nota">{progreso.nota}</p>}
          {tx && <a className="proceso-tx" href={tx} target="_blank" rel="noreferrer">{t("Ver transacción en el explorador ↗")}</a>}
        </div>
      ) : (
        <div className="proceso proceso-error">
          <div className="proceso-x" aria-hidden>!</div>
          <h3>{t("No se pudo completar")}</h3>
          <p className="detalle proceso-mensaje-error">{progreso.mensaje}</p>
        </div>
      )}
    </Modal>
  );
}
